import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { type IUser } from "@/types/(my-space)/users.interface.ts";
import type { ISingleEmployeeDocument } from "@/types/(company)/employees.interface.ts";

interface UserState {
  user: IUser | null;
  authChecked: boolean;
  isLastMFABackupCodeUsed: boolean;
  generatedEmployeeDocuments?: Array<ISingleEmployeeDocument>;
  unreadNotificationsCount: number;
  unreadAnnouncementCount: number;
  announcementId: number | null;
  notificationSettings: {
    isEmailNotificationEnabled: boolean;
    isPushNotificationEnabled: boolean;
    isSoundNotificationEnabled: boolean;
    isTextToSpeechNotificationEnabled: boolean;
  };
  isAdmin: boolean;
  showSplitScreen: boolean;
}

const initialState: UserState = {
  user: null,
  authChecked: false,
  isLastMFABackupCodeUsed: false, // If lost access to authenticator app and used all backup code (8/8) as well
  generatedEmployeeDocuments: [],
  unreadNotificationsCount: 0,
  unreadAnnouncementCount: 0,
  announcementId: null,
  notificationSettings: {
    isEmailNotificationEnabled: false,
    isPushNotificationEnabled: false,
    isSoundNotificationEnabled: false,
    isTextToSpeechNotificationEnabled: false,
  },
  isAdmin: false,
  showSplitScreen: false,
};

export const USER_SLICE_NAME = "user";

const userSlice = createSlice({
  name: USER_SLICE_NAME,
  initialState,
  reducers: {
    setUserSlice: (state, action) => {
      return { ...state, ...action.payload };
    },
    setAuthChecked(state, action: PayloadAction<boolean>) {
      state.authChecked = action.payload;
    },
    setIsLastMFABackupCodeUsed: (state, action) => ({
      ...state,
      isLastMFABackupCodeUsed: action.payload,
    }),
    restIsLastMFABackupCodeUsed: (state) => ({
      ...state,
      isLastMFABackupCodeUsed: false,
    }),
    setGeneratedEmployeeDocuments: (
      state,
      action: PayloadAction<Array<ISingleEmployeeDocument>>,
    ) => {
      state.generatedEmployeeDocuments = action.payload;
    },
    clearUser(state) {
      state.user = null;
    },
    setUnreadNotificationsCount: (state, action: PayloadAction<number>) => {
      state.unreadNotificationsCount = action.payload;
    },
    setUnreadAnnouncementCount: (state, action: PayloadAction<number>) => {
      state.unreadAnnouncementCount = action.payload;
    },
    setAnnouncementId: (state, action: PayloadAction<number | null>) => {
      state.announcementId = action.payload;
    },
    setNotificationSettings: (
      state,
      action: PayloadAction<Partial<UserState["notificationSettings"]>>,
    ) => {
      state.notificationSettings = {
        ...state.notificationSettings,
        ...action.payload,
      };
    },
    setIsAdmin: (state, action: PayloadAction<boolean>) => {
      state.isAdmin = action.payload;
    },
  },
});

export const {
  setUserSlice,
  setAuthChecked,
  clearUser,
  setIsLastMFABackupCodeUsed,
  restIsLastMFABackupCodeUsed,
  setGeneratedEmployeeDocuments,
  setUnreadNotificationsCount,
  setUnreadAnnouncementCount,
  setAnnouncementId,
  setNotificationSettings,
  setIsAdmin,
} = userSlice.actions;

export const selectUser = (state: { [USER_SLICE_NAME]: UserState }) =>
  state[USER_SLICE_NAME];
export default userSlice.reducer;
